import type { Ref } from 'react'
import type { Id } from '@/infra/id'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
} from '@/components/Dropdown'
import styles from './NoteCard.module.css'

export interface NoteCardProps {
  id: Id
  author: AvatarSubject
  displayName: string
  formattedTime: string
  role?: string
  mentions: string[]
  body: string
  canDelete: boolean
  onDelete: () => unknown
}

export function NoteCard({
  id,
  author,
  displayName,
  formattedTime,
  role,
  mentions,
  body,
  canDelete,
  onDelete,
}: NoteCardProps) {
  return (
    <div className={styles.root} data-test={`job-note-${id}`}>
      <span className={styles.avatar}>
        <Avatar size={24} subject={author} />
      </span>
      <div className={styles.content}>
        <div className={styles.meta}>
          <span className={styles.author} title={displayName}>
            {displayName}
          </span>
          {role && <span className={styles.role}>{role}</span>}
          <span className={styles.time}>{formattedTime}</span>
          {canDelete && (
            <Dropdown
              autoDismiss
              testId={`job-note-menu-${id}`}
              button={
                <IconButton
                  name="more"
                  label="Note actions"
                  className={styles.menu}
                />
              }
            >
              <DropdownItem icon={<Icon name="delete" />} onPress={onDelete}>
                <DropdownItemTitle>Delete</DropdownItemTitle>
              </DropdownItem>
            </Dropdown>
          )}
        </div>
        <p className={styles.body} data-private>
          {mentions.map((mention) => (
            <span key={mention} className={styles.mention} title={mention}>
              {mention}
            </span>
          ))}
          {body}
        </p>
      </div>
    </div>
  )
}

/**
 * A placeholder for a {@link NoteCard} still loading. Render it inside a
 * `SkeletonList` or `SkeletonListInfiniteScrollTrigger`.
 */
export function NoteCardSkeleton() {
  const { vary, varyPercent, ref, index } = useSkeletonListContext()
  return (
    <div
      ref={ref as Ref<HTMLDivElement> | undefined}
      className={styles.root}
      aria-hidden
    >
      <span className={styles.avatar}>
        <Skeleton block variant="circle" width={24} height={24} />
      </span>
      <div className={styles.content}>
        <div className={styles.meta}>
          <span className={styles.author}>
            <Skeleton width={vary(110)} />
          </span>
          <span className={styles.time}>
            <Skeleton width={56} />
          </span>
        </div>
        <p className={styles.body}>
          <Skeleton width={varyPercent(90)} />
          {index % 2 === 0 && <Skeleton width={varyPercent(45)} />}
        </p>
      </div>
    </div>
  )
}
